/**
 * Birch Rewards landing page — fills sections/rewards-landing.liquid from
 * Birch Rewards (OM's program, assets/birch-rewards.js) once it's Live, and
 * from Smile.js until then.
 *
 * Birch Rewards: points come off every order automatically at checkout, so the
 * coupon-code parts (the 3-step code band, reward codes, Smile panel buttons)
 * stay hidden and the copy that talks about codes is swapped for the
 * [data-rwlp-birch] / FAQ `birch_answer` versions. The earn-rate FAQ, the
 * signed-out card and the rate line reuse the Smile renderers below, fed the
 * program's numbers in Smile's shape (birchRules / birchProducts).
 *
 * For every visitor: the program's ways to earn and ways to redeem, and, signed
 * out, the program at a glance on the membership card. Smile.js serves earning
 * rules, points products and points settings without a customer, so none of
 * that needs a sign-in.
 *
 * Signed in: the customer's balance on the card, what it's worth (or how far
 * off the first reward is), their unused reward codes, a Redeem link on each
 * reward they can afford, and the hero button that fits their program state.
 *
 * Smile's app embed loads and initializes Smile.js itself and identifies the
 * Shopify customer — see assets/rewards-panel.js, which this mirrors. Nothing
 * is preloaded for a guest, so this preloads pointsSettings itself before
 * Smile.formatPoints is used.
 *
 * Plain IIFE, matching assets/rewards-panel.js.
 */

'use strict';

(function () {
  // How long to wait for Smile.js before settling on the fallback copy. The
  // page still fills in if Smile initializes after this.
  var READY_TIMEOUT_MS = 8000;
  // Codes listed on the page; "See all your rewards" opens the rest in Smile.
  var MAX_CODES = 6;
  var COUNT_UP_MS = 900;
  // Under the blurred sample balance when Smile has no step reward to price it.
  var GUEST_NOTE = 'Worth money off your next order';

  var root;
  var currency = 'USD';
  var locale = 'en';
  var started = false;

  function $(sel) { return root.querySelector(sel); }

  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function announce(text) {
    var el = $('[data-rwlp-status]');
    if (el) el.textContent = text;
  }

  function flashCopied(btn) {
    var prev = btn.getAttribute('data-label') || btn.textContent;
    btn.setAttribute('data-label', prev);
    btn.textContent = 'Copied!';
    btn.classList.add('is-copied');
    announce('Code copied.');
    setTimeout(function () {
      btn.textContent = prev;
      btn.classList.remove('is-copied');
    }, 1600);
  }

  function copy(text, btn) {
    var done = function () { flashCopied(btn); };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(function () { fallbackCopy(text); done(); });
    } else {
      fallbackCopy(text);
      done();
    }
  }

  function fallbackCopy(text) {
    var ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    try { document.execCommand('copy'); } catch (_e) { /* noop */ }
    document.body.removeChild(ta);
  }

  // Smile.js once its methods exist (they exist before it's initialized).
  function smile() {
    var S = window.Smile;
    return S && S.pointsSettings && S.earningRules && S.pointsProducts ? S : null;
  }

  // The identified customer, or null while Smile.js is absent/uninitialized.
  // customer.current() reads Smile's cache, so this makes no API call.
  function smileCustomer() {
    var S = window.Smile;
    if (!S || !S.customer || typeof S.customer.current !== 'function') return null;
    try { return S.customer.current(); } catch (_e) { return null; }
  }

  // Smile.formatPoints uses the program's own points name ("1 Point",
  // "2,400 Points") but needs pointsSettings preloaded; plain numbers otherwise.
  function fmt(n, withLabel) {
    try {
      return window.Smile.formatPoints(n, { showPointsLabel: withLabel !== false });
    } catch (_e) {
      var s = Number(n || 0).toLocaleString(locale);
      return withLabel === false ? s : s + (n === 1 ? ' point' : ' points');
    }
  }

  function pointsLabel(n) {
    var settings = null;
    try { settings = window.Smile.pointsSettings.preloaded(); } catch (_e) { /* noop */ }
    var label = settings && settings.pointsLabel;
    if (!label) return n === 1 ? 'Point' : 'Points';
    return n === 1 ? label.one : label.other;
  }

  // Smile's amounts are in the shop's currency; whole amounts drop the cents.
  function money(amount) {
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: amount % 1 ? 2 : 0,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch (_e) {
      return '$' + amount;
    }
  }

  function date(iso) {
    try {
      return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(iso));
    } catch (_e) {
      return String(iso).slice(0, 10);
    }
  }

  // Tier-restricted rules aren't the program everyone joins, so the page only
  // lists rules that apply to all tiers.
  function openRules(rules) {
    return (rules || []).filter(function (r) { return !(r.restrictedToVipTierIds || []).length; });
  }

  // The rule that pays per amount spent (Place an order: 5 Points per $1).
  function spendRule(rules) {
    return openRules(rules).filter(function (r) {
      return r.rewardValue && r.rewardValue.type === 'variable' && r.rewardValue.variable;
    })[0] || null;
  }

  // The reward that turns points into money off in steps (100 Points = $1 off).
  function stepProduct(products) {
    return (products || []).filter(isStep)[0] || null;
  }

  function isStep(p) {
    return p.exchangeType === 'variable' && p.variablePointsStep > 0 && p.variablePointsStepRewardValue > 0;
  }

  function stepMin(p) { return p.variablePointsMin || p.variablePointsStep; }

  function stepWorth(p, balance) {
    var usable = p.variablePointsMax ? Math.min(balance, p.variablePointsMax) : balance;
    if (usable < stepMin(p)) return 0;
    return Math.floor(usable / p.variablePointsStep) * p.variablePointsStepRewardValue;
  }

  function rewardName(p) { return (p.reward && p.reward.name) || 'a reward'; }

  // Fewest points each reward can be redeemed with, cheapest first.
  function redeemable(products) {
    return (products || []).map(function (p) {
      return { p: p, step: isStep(p), min: isStep(p) ? stepMin(p) : p.pointsPrice };
    }).filter(function (x) {
      return x.min > 0;
    }).sort(function (a, b) {
      return a.min - b.min;
    });
  }

  // "100 Points = $1 off" for a step reward, "500 Points = $5 off coupon" for
  // the cheapest fixed one.
  function rateText(products) {
    var p = stepProduct(products);
    if (p) return fmt(p.variablePointsStep) + ' = ' + money(p.variablePointsStepRewardValue) + ' off';
    var first = redeemable(products)[0];
    return first ? fmt(first.min) + ' = ' + rewardName(first.p) : '';
  }

  function earnValue(rule) {
    var v = rule.rewardValue || {};
    if (v.type === 'fixed' && v.fixed) return fmt(v.fixed.value);
    if (v.type === 'variable' && v.variable) return fmt(v.variable.value) + ' per ' + money(v.variable.perAmount);
    return '';
  }

  function earnLimit(rule) {
    var l = rule.earningLimit;
    if (!l || !l.max) return '';
    var times = l.max === 1 ? 'Once' : 'Up to ' + l.max + ' times';
    if (l.type === 'rolling' && l.rolling && l.rolling.unitCount) {
      return times + ' every ' + (l.rolling.unitCount === 1 ? 'day' : l.rolling.unitCount + ' days');
    }
    return l.max === 1 ? 'One time only' : times + ' in total';
  }

  function safeUrl(u) { return /^https?:\/\//i.test(u || '') ? u : ''; }

  function openLink(deepLink, html, cls) {
    return '<a class="' + cls + '" href="?smile_deep_link=' + encodeURIComponent(deepLink) + '"' +
      ' data-rwlp-open="' + esc(deepLink) + '">' + html + '</a>';
  }

  /* ── Ways to earn / Ways to redeem ── */

  function renderEarn(rules) {
    var list = openRules(rules);
    $('[data-rwlp-earn-wrap]').hidden = !list.length;
    $('[data-rwlp-earn]').innerHTML = list.map(function (r) {
      var limit = earnLimit(r);
      var url = safeUrl(r.actionUrl);
      return '<li class="rwlp-menu__row">' +
        '<span class="rwlp-menu__name">' + esc(r.name) + '</span>' +
        '<span class="rwlp-menu__leader" aria-hidden="true"></span>' +
        '<span class="rwlp-menu__value">' + esc(earnValue(r)) + '</span>' +
        (limit ? '<span class="rwlp-menu__meta">' + esc(limit) + '</span>' : '') +
        (url && r.actionText
          ? '<a class="rwlp-menu__action" href="' + esc(url) + '" target="_blank" rel="noopener">' + esc(r.actionText) + '</a>'
          : '') +
        '</li>';
    }).join('');
    return list.length;
  }

  // balance is null for a guest, who gets no Redeem links.
  function renderRedeem(products, balance) {
    var list = redeemable(products);
    $('[data-rwlp-redeem-wrap]').hidden = !list.length;
    $('[data-rwlp-redeem]').innerHTML = list.map(function (x) {
      var p = x.p;
      var name = rewardName(p);
      var value = x.step
        ? fmt(p.variablePointsStep) + ' = ' + money(p.variablePointsStepRewardValue) + ' off'
        : fmt(p.pointsPrice);
      var meta = [];
      if (p.reward && p.reward.description) meta.push(p.reward.description);
      if (x.step && p.variablePointsMax) meta.push('Spend up to ' + fmt(p.variablePointsMax) + ' at a time.');

      var action = '';
      if (balance != null && balance >= x.min) {
        action = openLink('points_product:' + p.id, 'Redeem<span class="rwlp-sr"> ' + esc(name) + '</span>', 'rwlp-menu__action');
      } else if (balance != null) {
        var pct = Math.max(0, Math.min(100, Math.round((balance / x.min) * 100)));
        action = '<span class="rwlp-menu__need"><span aria-hidden="true"><span style="width:' + pct + '%"></span></span>' +
          '<span>' + esc(fmt(x.min - balance)) + ' to go</span></span>';
      }

      return '<li class="rwlp-menu__row">' +
        '<span class="rwlp-menu__name">' + esc(name) + '</span>' +
        '<span class="rwlp-menu__leader" aria-hidden="true"></span>' +
        '<span class="rwlp-menu__value">' + esc(value) + '</span>' +
        (meta.length ? '<span class="rwlp-menu__meta">' + esc(meta.join(' ')) + '</span>' : '') +
        action + '</li>';
    }).join('');
    return list.length;
  }

  /* ── FAQ: how many points an order earns ── */

  function percent(ratio) {
    try {
      return new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 2 }).format(ratio);
    } catch (_e) {
      return Math.round(ratio * 10000) / 100 + '%';
    }
  }

  // "5 Points for every $1 you spend", then what that comes to in money off
  // using the step reward (100 Points = $1 off → $0.05 back per $1 = 5% back).
  // Hidden when there's no per-amount earning rule to describe.
  function renderEarnRate(rules, products) {
    var item = $('[data-rwlp-faq-rate]');
    if (!item) return;
    var rule = spendRule(rules);
    if (!rule) { item.hidden = true; return; }

    var v = rule.rewardValue.variable;
    var per = money(v.perAmount);
    var html = '<p>Every order earns <strong>' + esc(fmt(v.value) + ' for every ' + per + ' you spend') + '</strong>.';

    var p = stepProduct(products);
    if (p) {
      // Money off per unit spent, as a share of that spend.
      var back = v.value * p.variablePointsStepRewardValue / p.variablePointsStep;
      var ratio = back / v.perAmount;
      html += ' Points turn into money off at ' + esc(fmt(p.variablePointsStep) + ' = ' + money(p.variablePointsStepRewardValue) + ' off') +
        ', so every ' + esc(per) + ' you spend earns ' + esc(money(Math.round(back * 100) / 100)) + ' back. That’s <strong>' +
        esc(percent(ratio)) + ' back</strong> on every order.</p>';

      var spend = v.perAmount * 100;
      var earned = v.value * 100;
      var worth = Math.floor(earned / p.variablePointsStep) * p.variablePointsStepRewardValue;
      if (worth > 0) {
        html += '<p>For example, a ' + esc(money(spend)) + ' order earns ' + esc(fmt(earned)) + ', worth ' +
          esc(money(worth)) + ' off a future order.</p>';
      }
    } else {
      html += ' See Ways to redeem above for what your points are worth.</p>';
    }

    $('[data-rwlp-faq-rate-answer]').innerHTML = html;
    item.hidden = false;
  }

  /* ── Membership card ── */

  function setCard(fields) {
    if ('label' in fields) $('[data-rwlp-card-label]').textContent = fields.label;
    if ('holder' in fields) $('[data-rwlp-card-holder]').textContent = fields.holder;
    if ('unit' in fields) $('[data-rwlp-card-unit]').textContent = fields.unit;
    if ('note' in fields) $('[data-rwlp-card-note]').textContent = fields.note;
    if ('rate' in fields) {
      $('[data-rwlp-card-rate]').textContent = fields.rate;
      // Signed out, the row is just the earn rate and the points value —
      // nothing without them. Signed in it also carries the customer's name,
      // so it stays.
      if (root.getAttribute('data-signed-in') !== 'true') {
        $('[data-rwlp-card-row]').hidden = !fields.rate && !$('[data-rwlp-card-holder]').textContent;
      }
    }
    if ('chip' in fields) {
      var chip = $('[data-rwlp-chip]');
      chip.hidden = !fields.chip;
      chip.textContent = fields.chip || '';
    }
    var meter = $('[data-rwlp-meter]');
    meter.hidden = fields.pct == null;
    if (fields.pct != null) {
      // Next frame, so the width transition runs from where it was.
      requestAnimationFrame(function () {
        $('[data-rwlp-meter-fill]').style.width = Math.max(2, fields.pct) + '%';
      });
    }
  }

  function setNumber(to, animate) {
    var el = $('[data-rwlp-card-num]');
    var from = Number(el.getAttribute('data-value')) || 0;
    el.setAttribute('data-value', to);
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!animate || reduce || from === to) {
      el.textContent = fmt(to, false);
      return;
    }
    var t0 = null;
    function frame(t) {
      if (t0 === null) t0 = t;
      var k = Math.min(1, (t - t0) / COUNT_UP_MS);
      var eased = 1 - Math.pow(1 - k, 3);
      el.textContent = fmt(Math.round(from + (to - from) * eased), false);
      if (k < 1) requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);
  }

  // Signed out: a blurred sample balance (in the markup) and what it would be
  // worth, with the earn rate and the points value along the foot —
  // "Earn 5 Points per $1" · "100 Points = $1 off". The earn rate also ends
  // the hero lead.
  function renderGuestCard(rules, products) {
    var rule = spendRule(rules);
    var v = rule && rule.rewardValue.variable;
    var sample = Number($('[data-rwlp-card-num]').getAttribute('data-value')) || 0;
    var p = stepProduct(products);
    var worth = p ? stepWorth(p, sample) : 0;
    setCard({
      note: worth > 0 ? 'Worth ' + money(worth) + ' off your next order' : GUEST_NOTE,
      holder: v ? 'Earn ' + fmt(v.value) + ' per ' + money(v.perAmount) : '',
      rate: rateText(products),
    });
    var lede = $('[data-rwlp-lede-rate]');
    if (lede && v) lede.textContent = ' Every ' + money(v.perAmount) + ' you spend earns ' + fmt(v.value) + '.';
  }

  // Where a balance stands: worth money off now, or how far off the first
  // reward is (pct feeds the meter).
  function standing(products, balance) {
    var p = stepProduct(products);
    if (p) {
      var worth = stepWorth(p, balance);
      if (worth > 0) {
        return { canRedeem: true, note: 'Worth ' + money(worth) + ' off your next order', worth: 'worth ' + money(worth) + ' off' };
      }
      var min = stepMin(p);
      return {
        pct: Math.round((balance / min) * 100),
        note: fmt(min - balance) + ' until your first ' + money(stepWorth(p, min)) + ' off',
      };
    }
    var list = redeemable(products);
    var affordable = list.filter(function (x) { return x.min <= balance; });
    if (affordable.length) {
      var best = rewardName(affordable[affordable.length - 1].p);
      return { canRedeem: true, note: 'Enough for ' + best, worth: 'enough for ' + best };
    }
    if (list.length) {
      return {
        pct: Math.round((balance / list[0].min) * 100),
        note: fmt(list[0].min - balance) + ' until ' + rewardName(list[0].p),
      };
    }
    return { note: '' };
  }

  function showCta(which) {
    Array.prototype.forEach.call(root.querySelectorAll('[data-rwlp-cta]'), function (a) {
      a.hidden = a.getAttribute('data-rwlp-cta') !== which;
    });
  }

  // The hero lead, signed in: "You have 477 Points, worth $4 off." ahead of
  // the section's own copy, which is about creating a coupon code — so that
  // copy only follows when there's enough to create one.
  var memberBody = null;
  function setLede(lead, withBody) {
    var el = $('[data-rwlp-lede]');
    if (!el) return;
    if (memberBody === null) memberBody = el.textContent.trim();
    el.innerHTML = lead + (withBody && memberBody ? ' ' + esc(memberBody) : '');
    el.hidden = !el.innerHTML;
  }

  // The coupon steps band and the card's link to it are for members only.
  // The band is rendered hidden; this is the only place that shows it. The
  // hero's "How it works" points at it only while it is showing.
  function showRedeemHow(show, canRedeem) {
    var band = $('[data-rwlp-redeem-how]');
    if (band) band.hidden = !show;
    var how = $('[data-rwlp-how-link]');
    if (how) how.setAttribute('href', show && band ? '#rwlp-redeem' : '#rwlp-how');
    var use = $('[data-rwlp-card-use]');
    if (use) use.hidden = !(show && canRedeem);
  }

  function renderMember(customer, data, products, firstRender) {
    // Excluded from the program: nothing to earn or redeem.
    if (customer.state === 'disabled') {
      showCta(null);
      showRedeemHow(false);
      setLede('', false);
      $('[data-rwlp-card-num]').textContent = '';
      setCard({ label: '', unit: '', note: 'Rewards aren’t available on this account.', rate: '', chip: null });
      return;
    }

    var balance = (data.wallet && data.wallet.balance) || 0;
    var tier = data.vip && data.vip.vipTier;
    var candidate = customer.state === 'candidate';
    var s = standing(products, balance);

    var have = '<strong>' + esc(fmt(balance)) + (s.worth ? ', ' + esc(s.worth) : '') + '</strong>';
    if (candidate) {
      setLede(balance > 0 ? 'You have ' + have + '. Join free to turn them into money off.' : 'Join free to start earning points on every order.', false);
    } else if (s.canRedeem) {
      setLede('You have ' + have + '.', true);
    } else {
      setLede('You have ' + have + '.' + (s.note ? ' ' + esc(s.note) + '.' : ''), false);
    }
    showRedeemHow(!candidate, s.canRedeem);

    setNumber(balance, firstRender);
    var fields = {
      label: 'Your ' + pointsLabel(2),
      unit: '',
      note: s.note,
      rate: rateText(products),
      chip: tier ? tier.name : (candidate ? 'Not joined yet' : 'Member'),
      pct: s.pct,
    };
    if (candidate) {
      fields.note = balance > 0 ? 'Join to spend them on money off' : 'Join to start earning on every order';
      fields.pct = null;
    }
    setCard(fields);
    showCta(candidate ? 'join' : (s.canRedeem ? 'redeem' : 'open'));

    renderCodes(data.fulfillments);
    announce('You have ' + fmt(balance) + '. ' + fields.note + '.');
  }

  function showCardFallback() {
    // Signed out, the sample balance stays under its lock.
    if (root.getAttribute('data-signed-in') !== 'true') {
      setCard({ note: GUEST_NOTE });
      return;
    }
    $('[data-rwlp-card-num]').textContent = '';
    setCard({ unit: '', note: 'We couldn’t load your points just now.', rate: '' });
  }

  /* ── Create your coupon code ── */

  // From the step reward: "Every 100 Points is $1 off.", step 2's "Points go
  // in steps of 100, so 400 Points = $4 off." and "Points under 100 stay in
  // your balance until you earn more." Empty without a step reward.
  function renderRedeemHow(products) {
    var rate = $('[data-rwlp-redeem-rate]');
    if (!rate) return;
    var p = stepProduct(products);
    var example = $('[data-rwlp-redeem-example]');
    var left = $('[data-rwlp-redeem-left]');
    if (!p) {
      rate.textContent = example.textContent = left.textContent = '';
      return;
    }
    var step = p.variablePointsStep;
    var n = Math.max(stepMin(p), step * 4);
    if (p.variablePointsMax) n = Math.min(n, p.variablePointsMax);
    n = Math.floor(n / step) * step;
    rate.textContent = ' Every ' + fmt(step) + ' is ' + money(p.variablePointsStepRewardValue) + ' off.';
    example.textContent = ' Points go in steps of ' + fmt(step, false) +
      (n >= stepMin(p) ? ', so ' + fmt(n) + ' = ' + money(stepWorth(p, n)) + ' off.' : '.');
    left.textContent = ' ' + pointsLabel(2) + ' under ' + fmt(stepMin(p), false) + ' stay in your balance until you earn more.';
  }

  /* ── Reward codes ── */

  // Issued, unused and unexpired codes only. usedAt stays empty for rewards
  // Smile can't track usage on, so those remain listed until they expire.
  function liveCodes(fulfillments) {
    var now = Date.now();
    return (fulfillments || []).filter(function (f) {
      return f.fulfillmentStatus === 'issued' && f.code && !f.usedAt &&
        (!f.expiresAt || Date.parse(f.expiresAt) > now);
    });
  }

  function renderCodes(fulfillments) {
    var wrap = $('[data-rwlp-codes-wrap]');
    if (!wrap) return;
    var codes = liveCodes(fulfillments);
    wrap.hidden = !codes.length;
    $('[data-rwlp-codes]').innerHTML = codes.slice(0, MAX_CODES).map(function (f) {
      return '<li class="rwlp-ticket">' +
        '<div class="rwlp-ticket__main">' +
        '<span class="rwlp-ticket__name">' + esc(f.name) + '</span>' +
        '<span class="rwlp-ticket__code">' + esc(f.code) + '</span>' +
        (f.expiresAt ? '<span class="rwlp-ticket__meta">Expires ' + esc(date(f.expiresAt)) + '</span>' : '') +
        '</div>' +
        '<button type="button" class="rwlp-ticket__copy" data-copy="' + esc(f.code) + '"' +
        ' aria-label="Copy code ' + esc(f.code) + '">Copy</button>' +
        '</li>';
    }).join('');
    $('[data-rwlp-codes-more]').hidden = codes.length <= MAX_CODES;
  }

  /* ── Loading ── */

  function soft(p) { return p ? p.catch(function () { return null; }) : null; }

  function load() {
    var S = smile();
    if (!S) { showFallback(); return; }
    var firstRender = !started;
    started = true;

    var signedIn = root.getAttribute('data-signed-in') === 'true';
    var customer = signedIn ? smileCustomer() : null;
    var member = customer && customer.state !== 'disabled';

    var settingsReady = null;
    try {
      if (!S.pointsSettings.preloaded()) settingsReady = soft(S.preload(['pointsSettings']));
    } catch (_e) {
      settingsReady = soft(S.preload(['pointsSettings']));
    }

    // Separate calls rather than one Smile.preload(), so a resource that fails
    // can't take the others down with it (same as rewards-panel.js). .get()
    // always fetches fresh data, which the refresh after a redemption relies on.
    Promise.all([
      settingsReady,
      soft(S.earningRules.get()),
      soft(S.pointsProducts.get()),
      member ? soft(S.customerPointsWallet.get()) : null,
      member ? soft(S.rewardFulfillments.get()) : null,
      member ? soft(S.customerVipStatus.get()) : null,
    ]).then(function (r) {
      var rules = r[1];
      var products = r[2];
      var wallet = r[3];
      // Candidates join before they can redeem, so only members get the
      // per-reward Redeem links.
      var rowBalance = wallet && customer.state === 'member' ? wallet.balance || 0 : null;

      var earnCount = rules ? renderEarn(rules) : 0;
      var redeemCount = products ? renderRedeem(products, rowBalance) : 0;
      $('[data-rwlp-program]').hidden = !(earnCount || redeemCount);
      if (rules) renderEarnRate(rules, products || []);
      if (products) renderRedeemHow(products);

      if (customer && (wallet || customer.state === 'disabled')) {
        renderMember(customer, { wallet: wallet, fulfillments: r[4], vip: r[5] }, products || [], firstRender);
      } else if (signedIn) {
        // Signed in to Shopify, but Smile didn't identify them or their
        // balance failed to load.
        showCardFallback();
      } else if (rules) {
        renderGuestCard(rules, products || []);
      } else {
        showCardFallback();
      }
    });
  }

  function showFallback() {
    if (!started) showCardFallback();
  }

  // Smile's app embed initializes Smile.js on its own schedule. A signed-out
  // visitor has no customer to wait for, so the initialized event is the
  // signal — plus one probe call, for when this script runs after the event
  // has already fired.
  function whenSmileReady() {
    document.addEventListener('smile-js-initialized', load, { once: true });
    var S = smile();
    if (S) {
      try {
        S.pointsSettings.get().then(function () { if (!started) load(); }, function () { /* not ready yet */ });
      } catch (_e) { /* not ready yet */ }
    }
    setTimeout(showFallback, READY_TIMEOUT_MS);
  }

  // Refresh after Smile's panel closes, since the customer may have redeemed
  // points or joined in it. snippets/smile-launcher-scope.liquid keeps
  // `birch-rewards-open` on <body> while that panel is open.
  function refreshOnPanelClose() {
    var wasOpen = document.body.classList.contains('birch-rewards-open');
    new MutationObserver(function () {
      var open = document.body.classList.contains('birch-rewards-open');
      if (wasOpen && !open && smileCustomer()) load();
      wasOpen = open;
    }).observe(document.body, { attributes: true, attributeFilter: ['class'] });
  }

  /* ── Birch Rewards ── */

  var BIRCH_WAIT_MS = 3000;

  // OM's program in the shape the Smile renderers above read.
  function birchRules(p) {
    return [{ name: 'Place an order', rewardValue: { type: 'variable', variable: { value: p.earnPointsPerDollar, perAmount: 1 } } }];
  }
  function birchProducts(p) {
    return [{
      exchangeType: 'variable',
      variablePointsStep: p.redeemPointsPerDollar,
      variablePointsStepRewardValue: 1,
      variablePointsMin: Math.max(p.minPoints || 0, p.redeemPointsPerDollar),
      reward: { name: 'Money off any order' },
    }];
  }

  function row(name, value, meta) {
    return '<li class="rwlp-menu__row">' +
      '<span class="rwlp-menu__name">' + esc(name) + '</span>' +
      '<span class="rwlp-menu__leader" aria-hidden="true"></span>' +
      '<span class="rwlp-menu__value">' + esc(value) + '</span>' +
      (meta ? '<span class="rwlp-menu__meta">' + esc(meta) + '</span>' : '') +
      '</li>';
  }

  function renderBirchProgram(p) {
    var B = window.BirchRewards;
    var earn = [row('Place an order', B.formatPoints(p.earnPointsPerDollar) + ' per ' + money(1), 'Yours to use ' + B.releaseText(p) + '.')]
      .concat((p.bonuses || []).map(function (b) { return row(b.name, B.bonusText(b)); }));
    $('[data-rwlp-earn-wrap]').hidden = false;
    $('[data-rwlp-earn]').innerHTML = earn.join('');
    var min = p.minPoints > 0 ? ' Use them once you have ' + B.formatPoints(p.minPoints) + '.' : '';
    $('[data-rwlp-redeem-wrap]').hidden = false;
    $('[data-rwlp-redeem]').innerHTML = row(
      'Money off any order',
      B.formatPoints(p.redeemPointsPerDollar) + ' = ' + money(1) + ' off',
      'Taken off automatically at checkout, up to your order total — no code needed.' + min
    );
    $('[data-rwlp-program]').hidden = false;
    renderEarnRate(birchRules(p), birchProducts(p));
  }

  // Copy written for codes → its automatic-points version, where the section has one.
  function swapBirchCopy() {
    Array.prototype.forEach.call(root.querySelectorAll('[data-rwlp-birch]'), function (el) {
      el.textContent = el.getAttribute('data-rwlp-birch');
    });
    Array.prototype.forEach.call(root.querySelectorAll('template[data-rwlp-faq-birch]'), function (t) {
      var a = t.parentNode.querySelector('.rwlp-faq__a');
      if (a) a.innerHTML = t.innerHTML;
    });
    ['[data-rwlp-codes-wrap]', '[data-rwlp-redeem-how]', '[data-rwlp-card-use]'].forEach(function (sel) {
      var el = $(sel);
      if (el) el.hidden = true;
    });
    var how = $('[data-rwlp-how-link]');
    if (how) how.setAttribute('href', '#rwlp-how');
  }

  function renderBirchMember(m, p) {
    var B = window.BirchRewards;
    var worth = money(m.valueCents / 100);
    var pending = m.pendingPoints > 0 ? B.formatPoints(m.pendingPoints) + ' more on the way' : '';
    var usable = !m.blocked && m.points >= Math.max(1, p.minPoints || 0);
    var note;
    if (m.blocked) note = 'Points can’t be used on new orders right now — please contact us.';
    else if (usable) note = 'Worth ' + worth + (m.autoApply ? ' — comes off your next order automatically' : ' — saved for later');
    else if (m.points > 0) note = B.formatPoints(p.minPoints - m.points) + ' until you can use them';
    else note = pending ? 'Yours to use ' + B.releaseText(p) : 'Earn on your next order';
    setNumber(m.points, true);
    setCard({ label: 'Your Points', unit: '', note: note, rate: rateText(birchProducts(p)), chip: pending || 'Member', pct: null });

    var have = '<strong>' + esc(B.formatPoints(m.points)) + (m.valueCents > 0 ? ', worth ' + esc(worth) : '') + '</strong>';
    var lead = 'You have ' + have + '.';
    if (usable) {
      lead += m.autoApply ? ' They come off your next order automatically.' : ' You’re saving them for later — switch on “use automatically” in your profile to spend them.';
    }
    if (pending) lead += ' ' + esc(pending) + ', yours to use ' + esc(B.releaseText(p)) + '.';
    setLede(lead, false);

    // One button: their activity and the auto-apply switch, on the profile page.
    Array.prototype.forEach.call(root.querySelectorAll('[data-rwlp-cta]'), function (a) {
      var keep = a.getAttribute('data-rwlp-cta') === 'open';
      a.hidden = !keep;
      if (keep) {
        a.setAttribute('href', '/pages/account-dashboard#birch-rewards-panel');
        a.removeAttribute('data-rwlp-open');
        a.textContent = 'See my points activity';
      }
    });
    announce('You have ' + B.formatPoints(m.points) + '. ' + note + '.');
  }

  function loadBirch(p) {
    started = true;
    swapBirchCopy();
    renderBirchProgram(p);
    if (root.getAttribute('data-signed-in') !== 'true') {
      renderGuestCard(birchRules(p), birchProducts(p));
      return;
    }
    window.BirchRewards.me().then(function (m) {
      if (m) renderBirchMember(m, p);
      else showCardFallback();
    }).catch(showCardFallback);
  }

  /** cb(program) when Birch Rewards is Live; cb(null) → Smile. */
  function whenBirchReady(cb) {
    var done = false;
    function finish(p) { if (!done) { done = true; cb(p); } }
    function ask(B) {
      if (done || !B) return;
      B.program().then(finish, function () { finish(null); });
    }
    if (window.BirchRewards) ask(window.BirchRewards);
    else document.addEventListener('birch-rewards:ready', function () { ask(window.BirchRewards); }, { once: true });
    setTimeout(function () { if (!window.BirchRewards) finish(null); }, BIRCH_WAIT_MS);
  }

  function init() {
    root = document.querySelector('[data-rwlp]');
    if (!root) return;
    currency = root.getAttribute('data-currency') || currency;
    locale = root.getAttribute('data-locale') || locale;

    root.addEventListener('click', function (e) {
      var copyBtn = e.target.closest('[data-copy]');
      if (copyBtn) { copy(copyBtn.getAttribute('data-copy'), copyBtn); return; }

      // Open Smile's panel in place when Smile UI is on the page; otherwise
      // the link's ?smile_deep_link= reload opens it.
      var link = e.target.closest('[data-rwlp-open]');
      var ui = window.SmileUI;
      if (link && ui && typeof ui.openPanel === 'function') {
        e.preventDefault();
        ui.openPanel({ deep_link: link.getAttribute('data-rwlp-open') });
      }
    });

    whenBirchReady(function (p) {
      if (p) { loadBirch(p); return; }
      whenSmileReady();
      if (root.getAttribute('data-signed-in') === 'true') refreshOnPanelClose();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
